import { useCallback, useEffect, useState } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AdminRoles } from 'constants/permissions';
import { CC_STATUS_DISPLAY } from 'constants/statuses';
import { CC_STATUS_ICON_DISPLAY } from 'constants/statuses';
import { getCheckTypeIcon } from 'helpers/iconsHelper';
import { useLocalStorage } from 'hooks/use-local-storage';
import AdminPage from 'layouts/AdminPage/AdminPage';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import Moment from 'react-moment';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { debounce } from 'utils/requestHelpers';

import { Button } from 'components/FormComponents';
import { IconComponent } from 'components/Icon/IconComponent';
import StatusPill from 'components/Pills/StatusPills';
import { Table } from 'components/Table/Table';
import { CustomToast } from 'components/Toast/CustomToast';

import { useGetSupportUsersStatsQuery } from 'api/admin-roles-api';
import { selectAuthentication } from 'api/authentication-slice';
import { useLazyGetAllCandidateChecksQuery } from 'api/candidate-checks-api';
import { useGetCheckTypesQuery } from 'api/check-types-api';
import { useGetAllCompaniesListQuery } from 'api/company-api';
import { useLazyGetACICExportFileQuery } from 'api/services-api';
import { getUser, selectUser } from 'api/user-slice';

import { CandidateCheckNotesModal } from '../AdminCandidateChecks/AdminCandidateCheckNotesModal';
import { AdminSupportStats } from './AdminSupportStats';

const AdminSupportDashboard = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const [candidateChecks, setCandidateChecks] = useState([]);
  const [companiesOptions, setCompaniesOptions] = useState([]);
  const [selectedCheckId, setSelectedCheckId] = useState(null);
  const [showNotes, setShowNotes] = useState(false);

  const [getACICExportFile] = useLazyGetACICExportFileQuery();
  const [getAllCandidateChecks] = useLazyGetAllCandidateChecksQuery();
  const { data: companiesData } = useGetAllCompaniesListQuery();
  const { data: checkTypes } = useGetCheckTypesQuery();

  const [checkType, setCheckType] = useState();

  const [stats, setStats] = useState({
    total_checks: 0,
    completed: 0,
    overdue: 0,
    submitted: 0,
    canceled: 0,
    in_progress: 0,
    assigned_last_24h: 0,
  });

  const { data: supportUsersStats, refetch: refetchSupportUsersStats } =
    useGetSupportUsersStatsQuery();

  const [submittedChecks, setSubmittedChecks] = useLocalStorage('support_user_total_checks', 0);

  useEffect(() => {
    if (supportUsersStats) {
      const lastSubmittedChecks = Number(supportUsersStats.result?.total_checks);
      setStats(supportUsersStats.result);
      if (lastSubmittedChecks > submittedChecks) {
        toast.custom(
          t => (
            <CustomToast
              t={t}
              title="New checks"
              message={`${lastSubmittedChecks - submittedChecks} new check(s) assigned to you. Hope you are having a good day!`}
              type="info"
            />
          ),
          {
            duration: 10000,
          },
        );
      }
      setSubmittedChecks(lastSubmittedChecks);
    }
  }, [supportUsersStats]);

  const { user_id, admin_role } = useSelector(selectAuthentication);
  const user = useSelector(selectUser);

  const [pagination, setPagination] = useState({
    page: 1,
    records_per_page: 15,
    total_pages: 1,
    total_records: 0,
  });

  const OVERDUE_SUPPORT_HOURS = 15;

  useEffect(() => {
    if (!companiesData) return;

    setCompaniesOptions([
      { name: t('common.all', { element: 'companies' }), value: 'all' },
      ...companiesData.result.map(c => ({ name: c.name, value: c.id })),
    ]);
  }, [companiesData]);

  useEffect(() => {
    if (!user?.id && user_id) {
      dispatch(getUser(user_id));
    }
    refetchSupportUsersStats();
  }, [user_id, user, dispatch]);

  const fetchCandidateChecks = debounce(async params => {
    setLoading(true);
    try {
      const { result } = await getAllCandidateChecks({
        ...params,
        user: user_id, // This will use the with_user scope to get checks assigned to this user
        overdue_support_hours: OVERDUE_SUPPORT_HOURS,
      }).unwrap();
      setPagination(result?.pagination);
      setCandidateChecks(result?.candidate_checks);
      if (params?.check_type && params?.check_type !== 'all')
        setCheckType((checkTypes || [])?.find(checkType => checkType.slug === params?.check_type));
      setLoading(false);
    } catch (error) {
      toast.custom(
        t => (
          <CustomToast
            t={t}
            title="Error fetching checks"
            message={String(
              error?.data?.message || error?.data?.error || error?.message || 'Unknown error',
            )}
            type="error"
          />
        ),
        {
          duration: 5000,
        },
      );
    } finally {
      setLoading(false);
    }
  }, 500);

  const formatMySqlUtcToLocalDate = ts => {
    if (!ts) return null;
    return new Date(ts.replace(' ', 'T') + 'Z');
  };

  const mapCCStatus = useCallback(status => {
    return CC_STATUS_DISPLAY[status]?.color || 'neutral';
  }, []);

  const handleDownloadACICExportFile = async () => {
    setLoading(true);
    await getACICExportFile({}).unwrap();
    setLoading(false);
  };

  return (
    <AdminPage
      loading={loading}
      title="Dashboard"
      subtitle={`Welcome, ${user?.first_name} ${user?.last_name}`}
      allowedRoles={[AdminRoles.Support, AdminRoles.ACIC, AdminRoles.SuperAdmin]}>
      {checkTypes?.length > 0 && (
        <>
          <div className="u-margin--large">
            <AdminSupportStats stats={stats} />
          </div>
          <Table.Context
            onQueryUpdate={fetchCandidateChecks}
            defaultQuery={{
              page: 1,
              status: {
                value: ['submitted', 'in-progress'],
                name: [t('common.submitted'), t('common.inProgress')],
                type: 'status',
              },
              in_progress_sub_status: {
                value: [
                  'processing',
                  'waiting_on_client',
                  'waiting_on_candidate',
                  'waiting_on_provider',
                  'deferred',
                ],
                name: [
                  'Processing',
                  'Waiting on Client',
                  'Waiting on Candidate',
                  'Waiting on Provider',
                  'Deferred',
                ],
                type: 'status',
              },
            }}>
            <Table.TableContent className="u-margin--large">
              {admin_role === AdminRoles.ACIC && (
                <Table.ActionButtons>
                  <Button icon="download" onClick={handleDownloadACICExportFile}>
                    Download ACIC XML
                  </Button>
                </Table.ActionButtons>
              )}
              <Table.Filters>
                <Table.LeftFilters>
                  <Table.Search placeholder="Search for a candidate..." />
                </Table.LeftFilters>
                <Table.RightFilters>
                  <Table.Filter
                    title="Company"
                    filterName="company"
                    options={companiesOptions}
                    useDefault={false}
                    useSearch={true}
                  />
                  <Table.MultiSelectFilter
                    title="Type"
                    filterName="check_type"
                    options={[
                      { name: t('common.all', { element: 'types' }), value: 'all' },
                      ...checkTypes?.map(checkType => ({
                        name: checkType.title,
                        value: checkType.slug,
                      })),
                    ]}
                    useSearchInMultiSelect={true}
                  />
                  <Table.MultiSelectFilter
                    title="Status"
                    filterName="status"
                    type="status"
                    options={[
                      { name: t('common.inProgress'), value: 'in-progress' },
                      { name: t('common.submitted'), value: 'submitted' },
                      { name: t('common.completed'), value: 'completed' },
                      { name: t('common.candidate'), value: 'candidate' },
                      { name: t('common.canceled'), value: 'canceled' },
                    ]}
                  />
                  <Table.MultiSelectFilter
                    title="In-progress status"
                    filterName="in_progress_sub_status"
                    type="status"
                    useDefault={false}
                    options={[
                      { name: 'Processing', value: 'processing' },
                      { name: 'Waiting on Client', value: 'waiting_on_client' },
                      { name: 'Waiting on Candidate', value: 'waiting_on_candidate' },
                      { name: 'Waiting on Provider', value: 'waiting_on_provider' },
                      { name: 'Blocked', value: 'blocked' },
                      { name: 'Deferred', value: 'deferred' },
                    ]}
                  />
                  <Table.MultiSelectFilter
                    title="Bot Status"
                    filterName="bot_status"
                    useDefault={false}
                    options={[
                      { name: 'Bot Failed', value: 'failed' },
                      { name: 'Successfully Submitted', value: 'submitted' },
                    ]}
                  />
                  <Table.MultiSelectFilter
                    title="Overdue Status"
                    filterName="overdue_status"
                    useDefault={false}
                    options={[
                      {
                        name: `Overdue Support (${OVERDUE_SUPPORT_HOURS}h)`,
                        value: 'overdue_support',
                      },
                      { name: 'Overdue Provider', value: 'overdue_provider' },
                    ]}
                  />
                </Table.RightFilters>
              </Table.Filters>
              <Table className="u-margin--large">
                <Table.Head>
                  <Table.Row key="header-row">
                    <Table.Header key="id">{t('common.id')}</Table.Header>
                    <Table.Header key="name" sortKey="candidates.first_name">
                      {t('common.name')}
                    </Table.Header>
                    <Table.Header key="type">{t('common.checks')}</Table.Header>
                    <Table.Header key="status">{t('common.status')}</Table.Header>
                    <Table.Header key="submitted" sortKey="candidate_checks.submitted_at">
                      {t('common.submittedAt')}
                    </Table.Header>
                    <Table.Header key="completed" sortKey="candidate_checks.completed_at">
                      {t('common.completedAt')}
                    </Table.Header>
                    <Table.Header key="internal_history">Notes</Table.Header>
                  </Table.Row>
                </Table.Head>
                <Table.Body>
                  {candidateChecks.map((candidateCheck, index) => {
                    const currentCheckType =
                      checkType ||
                      checkTypes.find(checkType => checkType.slug === candidateCheck?.check_type);
                    return (
                      <Table.Row key={`check-${candidateCheck.id}-${index}`}>
                        <Table.Col>
                          <Link
                            to={`/admin/candidates/${candidateCheck.candidate_id}`}
                            className="u-link dark">
                            {candidateCheck.candidate_id}
                          </Link>
                        </Table.Col>
                        <Table.Col>
                          <div className="d-flex flex-column u-gap-small align-items-start">
                            {`${candidateCheck.first_name} ${candidateCheck.last_name}`}
                            <span className="u-font-size-x-small secondary-text">
                              {candidateCheck.email}
                            </span>
                          </div>
                        </Table.Col>
                        <Table.Col>
                          <IconComponent
                            size="small"
                            tip={currentCheckType.title}
                            noBackground
                            type={CC_STATUS_ICON_DISPLAY[candidateCheck.status].color}
                            icon={getCheckTypeIcon(currentCheckType?.slug)}
                          />
                        </Table.Col>
                        <Table.Col>
                          <div className="d-flex flex-wrap gap-1 align-items-center">
                            <StatusPill
                              text={
                                candidateCheck.status === 'in-progress'
                                  ? candidateCheck.in_progress_sub_status
                                  : candidateCheck.status
                              }
                              status={mapCCStatus(candidateCheck.status)}
                            />

                            {candidateCheck.bot_status === 'failed' && (
                              <StatusPill text="bot failed" status="error" />
                            )}
                            {candidateCheck.bot_status === 'submitted' && (
                              <StatusPill text="successfully submitted" status="success" />
                            )}
                            {!!candidateCheck?.overdue_support && (
                              <StatusPill text={`overdue support`} status={'error'} />
                            )}
                            {!!candidateCheck?.overdue_provider && (
                              <StatusPill text={`overdue provider`} status={'error'} />
                            )}
                          </div>
                        </Table.Col>
                        <Table.Col>
                          {candidateCheck.submitted_at && (
                            <Moment format="MMMM Do, YYYY HH:mm">
                              {formatMySqlUtcToLocalDate(candidateCheck?.submitted_at)}
                            </Moment>
                          )}
                        </Table.Col>
                        <Table.Col>
                          <div className="d-flex u-gap-small align-items-center">
                            {candidateCheck.completed_at && (
                              <Moment format="MMMM Do, YYYY HH:mm">
                                {formatMySqlUtcToLocalDate(candidateCheck?.completed_at)}
                              </Moment>
                            )}

                            {candidateCheck.upload_id_status === 'no_upload' && (
                              <OverlayTrigger overlay={<Tooltip>No upload</Tooltip>}>
                                <FontAwesomeIcon
                                  icon="file-circle-xmark"
                                  className="u-txt-status-overdue"
                                />
                              </OverlayTrigger>
                            )}
                          </div>
                        </Table.Col>
                        <Table.Col>
                          <div
                            className="u-link dark"
                            onClick={() => {
                              setSelectedCheckId(candidateCheck?.candidate_check_id);
                              setShowNotes(true);
                            }}>
                            Click for notes
                          </div>
                        </Table.Col>
                      </Table.Row>
                    );
                  })}
                </Table.Body>
                <Table.Pagination {...pagination} />
              </Table>
            </Table.TableContent>
          </Table.Context>
        </>
      )}
      {selectedCheckId && (
        <CandidateCheckNotesModal
          checkId={selectedCheckId}
          visible={showNotes}
          onClose={() => setShowNotes(false)}
        />
      )}
    </AdminPage>
  );
};

export default AdminSupportDashboard;
